import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Box, Typography, Container } from '@mui/material';
import Button from '@mui/material/Button';
import { sharedQuiltsService, SharedQuilt } from '../lib/supabase';
import { QuiltVisualization } from './QuiltVisualization';
import { QuiltCalculations } from '../utils/quiltCalculations';
interface GlobalRecentPlansProps {
  onPlanSelect?: (planData: SharedQuilt['data']) => void;
}
function makeDisplayTitle(sharedQuilt: any): string {
  // 1) Try known title fields
  const raw = typeof sharedQuilt?.title === 'string' && sharedQuilt.title || typeof sharedQuilt?.summary?.title === 'string' && sharedQuilt.summary.title || typeof sharedQuilt?.plan?.title === 'string' && sharedQuilt.plan.title || typeof sharedQuilt?.metadata?.title === 'string' && sharedQuilt.metadata.title || '';
  if (raw.trim()) {
    const cleaned = raw.replace(/quilt/gi, '').trim();
    return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : 'Quilt';
  }

  // 2) No explicit title? Synthesize one from data
  const sizeName = sharedQuilt?.plan?.mattressSize ?? sharedQuilt?.summary?.mattressSize ?? sharedQuilt?.plan?.throwSize ?? sharedQuilt?.summary?.throwSize;
  const kind = sharedQuilt?.summary?.quiltType ?? sharedQuilt?.plan?.quiltType; // e.g., 'bed', 'throw', 'crib'

  const w = sharedQuilt?.summary?.finishedSize?.width ?? sharedQuilt?.plan?.result?.quilt?.finished?.width;
  const h = sharedQuilt?.summary?.finishedSize?.height ?? sharedQuilt?.plan?.result?.quilt?.finished?.height;
  const parts = [sizeName,
  // e.g., 'Queen'
  kind && kind !== 'bed' ? kind : 'quilt',
  // 'throw' or default 'quilt'
  w && h ? `(${Math.round(w)}" × ${Math.round(h)}")` : null].filter(Boolean);

  // Final fallback
  return parts.join(' ') || 'Quilt';
}

// Quilt thumbnail component for global plans
const GlobalQuiltThumbnail = ({
  sharedQuilt
}: {
  sharedQuilt: SharedQuilt;
}) => {
  const calculations = new QuiltCalculations(sharedQuilt.data);
  const results = calculations.calculate();
  return <Box sx={{
    width: 80,
    minHeight: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}>
      <QuiltVisualization blocksAcross={results.blocksAcross} blocksDown={results.blocksDown} quiltWidth={results.quiltSize?.width} quiltHeight={results.quiltSize?.height} hasSashing={sharedQuilt.data.sashing !== 'none' && !!sharedQuilt.data.sashing} hasCornerstones={!!sharedQuilt.data.cornerstones} blockSize={results.finishedBlockSize} sashingSize={results.sashingSize} borderSizes={results.borderSizes} borderSize={results.borderSize} />
    </Box>;
};
export const GlobalRecentPlans = ({
  onPlanSelect
}: GlobalRecentPlansProps) => {
  const [recentSharedQuilts, setRecentSharedQuilts] = useState<SharedQuilt[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    const loadSharedQuilts = async () => {
      try {
        const quilts = await sharedQuiltsService.getRecentSharedQuilts(3);
        setRecentSharedQuilts(quilts);
      } catch (error) {
        console.error('Failed to load shared quilts:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSharedQuilts();

    // Refresh when the page becomes visible (user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadSharedQuilts();
      }
    };

    // Listen for new plans being saved to refresh immediately
    const handleNewPlanSaved = () => {
      loadSharedQuilts();
    };

    // Auto-refresh every 60 seconds to show new plans from other users
    const refreshInterval = setInterval(() => {
      loadSharedQuilts();
    }, 60000);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('globalPlanSaved', handleNewPlanSaved);
    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('globalPlanSaved', handleNewPlanSaved);
    };
  }, []);
  const handlePlanClick = (sharedQuilt: SharedQuilt) => {
    if (onPlanSelect) {
      onPlanSelect(sharedQuilt.data);
    }
    // Navigate directly to the unique plan URL
    navigate(`/plan/${sharedQuilt.id}`);
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    if (diffInHours < 24) {
      if (diffInHours === 0) return "Created by a quilter just now";
      return `Created by a quilter ${diffInHours}h ago`;
    }
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return "Created by a quilter yesterday";
    if (diffInDays < 7) return `Created by a quilter ${diffInDays}d ago`;
    return `Created by a quilter on ${date.toLocaleDateString()}`;
  };
  if (loading) {
    return <Box sx={{
      py: 8,
      backgroundColor: 'primary.light',
      minHeight: '400px'
    }}>
        <Container maxWidth="lg" sx={{
        px: 2
      }}>
          <Typography variant="h2" component="h2" sx={{
          textAlign: 'center',
          mb: 6,
          fontSize: '2rem'
        }}>
            Preview a quilt plan
          </Typography>
          <Box sx={{
          maxWidth: '64rem',
          mx: 'auto'
        }}>
            <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)'
            },
            gap: 2
          }}>
              {[1, 2, 3].map(index => <Card key={index} variant="outlined" sx={{
              animation: 'pulse 2s infinite'
            }}>
                  <CardContent sx={{
                p: 4,
                textAlign: 'center',
                minHeight: '280px',
                display: 'flex',
                flexDirection: 'column'
              }}>
                    <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 3,
                  py: 2,
                  flexShrink: 0
                }}>
                      <Box sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: 'divider',
                    borderRadius: 1
                  }}></Box>
                    </Box>
                     <Box sx={{
                  height: 24,
                  backgroundColor: 'divider',
                  borderRadius: 1,
                  mb: 1
                }}></Box>
                     <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5
                }}>
                       <Box sx={{
                    height: 16,
                    backgroundColor: 'divider',
                    borderRadius: 1
                  }}></Box>
                       <Box sx={{
                    height: 16,
                    backgroundColor: 'divider',
                    borderRadius: 1
                  }}></Box>
                    </Box>
                  </CardContent>
                </Card>)}
            </Box>
          </Box>
        </Container>
      </Box>;
  }
  if (recentSharedQuilts.length === 0) {
    return <Box sx={{
      py: 8,
      backgroundColor: 'primary.light',
      minHeight: '400px'
    }}>
        <Container maxWidth="lg" sx={{
        px: 2
      }}>
          <Typography variant="h2" component="h2" sx={{
          textAlign: 'center',
          mb: 6,
          fontSize: '2rem'
        }}>
            Preview a quilt plan
          </Typography>
          <Box sx={{
          maxWidth: '64rem',
          mx: 'auto'
        }}>
            <Box sx={{
            textAlign: 'center',
            color: 'text.secondary'
          }}>
              <Typography>No recent quilt plans to display yet.</Typography>
            </Box>
          </Box>
        </Container>
      </Box>;
  }
  return <Box sx={{
    py: 8,
    backgroundColor: 'primary.light'
  }}>
      <Container maxWidth="lg" sx={{
      px: 2
    }}>
        <Typography variant="h2" component="h2" sx={{
        textAlign: 'center',
        mb: 2,
        fontSize: '2rem'
      }}>What's in a quilt plan?</Typography>
        
        <Typography variant="body1" sx={{
        color: 'text.secondary',
        textAlign: 'center',
        mb: 6
      }}>Calculate how much fabric you'll need, how to cut it, and what it will cost.</Typography>
        
        <Box sx={{
        maxWidth: '64rem',
        mx: 'auto'
      }}>
          <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)'
          },
          gap: 2
        }}>
            {recentSharedQuilts.map(sharedQuilt => <Card key={sharedQuilt.id} variant="outlined" sx={{
            cursor: 'pointer',
            '&:hover': {
              boxShadow: 3
            },
            transition: 'box-shadow 0.2s'
          }} onClick={() => handlePlanClick(sharedQuilt)}>
                <CardContent sx={{
              p: 4,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              py: 3
            }}>
                  <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 3,
                py: 2.5,
                minHeight: 140,
                alignItems: 'center',
                flexShrink: 0
              }}>
                    <GlobalQuiltThumbnail sharedQuilt={sharedQuilt} />
                  </Box>
                  <Typography variant="h3" sx={{
                mb: 1,
                color: 'primary.main'
              }}>
                    {(() => {
                  // Match the format from the plan page: capitalize(sizeName) + purposeText
                  const data = sharedQuilt.data;

                  // Get size name
                  let sizeName = '';
                  if (data.bedSize) {
                    sizeName = data.bedSize.toLowerCase();
                    if (sizeName === 'full-double') {
                      sizeName = 'full/double';
                    }
                  } else if (data.throwSize) {
                    sizeName = data.throwSize;
                  }

                  // Get purpose text
                  const purposeText = data.purpose === 'bed-cover' ? 'bed cover' : 'throw blanket';

                  // Capitalize and combine
                  return `${sizeName.charAt(0).toUpperCase() + sizeName.slice(1)} ${purposeText}`;
                })()}
                  </Typography>
                  <Box sx={{
                color: 'text.secondary',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5
              }}>
                    <Typography variant="body1" sx={{
                  color: 'text.secondary'
                }}>{formatDate(sharedQuilt.created_at)}</Typography>
                  </Box>
                  
                </CardContent>
              </Card>)}
          </Box>
        </Box>
      </Container>
    </Box>;
};